import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useTenant } from '@/contexts/TenantContext';

export interface PipelineStage {
  id: string;
  name: string;
  color: string;
  orderIndex: number;
  probability: number;
  isCloseWon: boolean;
  isCloseLost: boolean;
  stageType?: string | null; // new, contacted, meeting, proposal, won, lost
  _count?: {
    leads: number;
  };
}

export interface Pipeline {
  id: string;
  name: string;
  isDefault: boolean;
  createdAt: string;
  stages: PipelineStage[];
}

export interface CreatePipelineDto {
  name: string;
  isDefault?: boolean;
}

export interface CreateStageDto {
  name: string;
  color?: string;
  probability?: number;
  isCloseWon?: boolean;
  isCloseLost?: boolean;
}

export function usePipelines() {
  const { currentTenant } = useTenant();
  
  return useQuery({
    queryKey: ['pipelines', currentTenant?.id],
    queryFn: () => api.get<Pipeline[]>('/pipelines'),
    enabled: !!currentTenant,
  });
}

export function usePipeline(id: string) {
  const { currentTenant } = useTenant();
  
  return useQuery({
    queryKey: ['pipeline', id, currentTenant?.id],
    queryFn: () => api.get<Pipeline>(`/pipelines/${id}`),
    enabled: !!id && !!currentTenant,
  });
}

export function useDefaultPipeline(projectId?: string) {
  const { currentTenant } = useTenant();
  
  const path = projectId 
    ? `/pipelines/default?projectId=${projectId}` 
    : '/pipelines/default';
  
  return useQuery({
    queryKey: ['pipeline', 'default', currentTenant?.id, projectId],
    queryFn: () => api.get<Pipeline>(path),
    enabled: !!currentTenant,
  });
}

export function useCreatePipeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePipelineDto) => api.post<Pipeline>('/pipelines', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelines'] });
    },
  });
}

export function useCreateStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pipelineId, data }: { pipelineId: string; data: CreateStageDto }) =>
      api.post<PipelineStage>(`/pipelines/${pipelineId}/stages`, data),
    onSuccess: (_, { pipelineId }) => {
      queryClient.invalidateQueries({ queryKey: ['pipelines'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline', pipelineId] });
    },
  });
}

export function useReorderStages() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pipelineId, stageIds }: { pipelineId: string; stageIds: string[] }) =>
      api.put<Pipeline>(`/pipelines/${pipelineId}/stages/reorder`, { stageIds }),
    onSuccess: (_, { pipelineId }) => {
      queryClient.invalidateQueries({ queryKey: ['pipelines'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline', pipelineId] });
    },
  });
}
